import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveDonationToFirestore } from '../../services/donationService';
import { getCurrentUser, getCurrentUserData } from '../../services/authService';
import RazorpayCheckout from '../RazorpayCheckout/RazorpayCheckout';
import PayPalCheckout from '../PayPalCheckout/PayPalCheckout';
import './DashboardDonationModal.css';

interface DashboardDonationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const DashboardDonationModal: React.FC<DashboardDonationModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const [selectedAmount, setSelectedAmount] = useState('1000');
    const [customAmount, setCustomAmount] = useState('');
    const [selectedProject, setSelectedProject] = useState('General Donation');
    const [paymentGateway, setPaymentGateway] = useState<'razorpay' | 'paypal'>('razorpay');
    const [isProcessing, setIsProcessing] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [userData, setUserData] = useState<any>(null);

    React.useEffect(() => {
        if (isOpen) {
            loadUserData();
        }
    }, [isOpen]);

    const loadUserData = async () => {
        const data = await getCurrentUserData();
        setUserData(data);
    };

    if (!isOpen) return null;

    const amountOptions = [
        { value: '500', label: '₹500', usd: '$6' },
        { value: '1000', label: '₹1,000', usd: '$12' },
        { value: '2500', label: '₹2,500', usd: '$30' },
        { value: '5000', label: '₹5,000', usd: '$60' },
        { value: '10000', label: '₹10,000', usd: '$120' },
        { value: 'custom', label: t('dashboard.donationModal.custom'), usd: '' }
    ];

    const projectOptions = [
        { key: 'generalDonation', label: t('dashboard.donationModal.projects.generalDonation') },
        { key: 'childEducation', label: t('dashboard.donationModal.projects.childEducation') },
        { key: 'cleanWater', label: t('dashboard.donationModal.projects.cleanWater') },
        { key: 'healthcare', label: t('dashboard.donationModal.projects.healthcare') },
        { key: 'foodNutrition', label: t('dashboard.donationModal.projects.foodNutrition') }
    ];

    const getFinalAmount = () => {
        if (selectedAmount === 'custom') {
            return parseFloat(customAmount) || 0;
        }
        return parseFloat(selectedAmount);
    };

    const getUSDAmount = () => {
        const inrAmount = getFinalAmount();
        return (inrAmount / 83).toFixed(2); // Approximate conversion
    };

    const saveDonation = async (paymentDetails: any) => {
        try {
            const user = getCurrentUser();
            if (!user || !userData) {
                alert('User session expired. Please log in again.');
                return;
            }

            const donationData = {
                userId: user.uid,
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                phone: userData.phone || '',
                project: selectedProject,
                amount: paymentGateway === 'razorpay' ? getFinalAmount() : parseFloat(getUSDAmount()),
                donationType: 'registered' as const,
                donationFrequency: 'one-time' as const,
                paymentMethod: 'card' as const, // Store as 'card' for compatibility
                paymentStatus: 'success',
                currency: paymentGateway === 'razorpay' ? 'INR' : 'USD',
                transactionId: paymentDetails.transactionId || paymentDetails.id,
                paymentGateway: paymentGateway,
                paymentDetails: JSON.stringify(paymentDetails)
            };

            const donationId = `DON${Date.now()}`;

            const result = await saveDonationToFirestore({
                ...donationData,
                donationId
            });

            if (result.success) {
                setShowSuccess(true);
                setTimeout(() => {
                    setShowSuccess(false);
                    onSuccess(); // Refresh the donations list
                    onClose();
                    // Reset form
                    setSelectedAmount('1000');
                    setCustomAmount('');
                    setSelectedProject('General Donation');
                    setPaymentGateway('razorpay');
                }, 2500);
            } else {
                alert('Failed to save donation: ' + result.error);
                setIsProcessing(false);
            }
        } catch (error) {
            console.error('Error saving donation:', error);
            alert('An error occurred while saving your donation');
            setIsProcessing(false);
        }
    };

    const handleRazorpaySuccess = (response: any) => {
        setIsProcessing(true);
        saveDonation({
            transactionId: response.razorpay_payment_id,
            orderId: response.razorpay_order_id,
            signature: response.razorpay_signature
        });
    };

    const handleRazorpayError = (error: any) => {
        console.error('Razorpay Error:', error);
        alert('Payment failed: ' + (error.description || 'Unknown error'));
        setIsProcessing(false);
    };

    const handleRazorpayCancel = () => {
        console.log('Payment cancelled by user');
        setIsProcessing(false);
    };

    const handlePayPalSuccess = (details: any) => {
        setIsProcessing(true);
        saveDonation({
            id: details.id,
            transactionId: details.purchase_units[0].payments.captures[0].id,
            status: details.status,
            payer: details.payer
        });
    };

    const handlePayPalError = (error: any) => {
        console.error('PayPal Error:', error);
        alert('Payment failed. Please try again.');
        setIsProcessing(false);
    };

    const handlePayPalCancel = () => {
        console.log('PayPal payment cancelled');
        setIsProcessing(false);
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !isProcessing) {
            onClose();
        }
    };

    if (showSuccess) {
        return (
            <div className="donation-modal-overlay" onClick={handleOverlayClick}>
                <div className="donation-modal">
                    <div className="success-message">
                        <div className="success-icon">✅</div>
                        <h2 className="success-title">{t('dashboard.donationModal.success.title')}</h2>
                        <p className="success-text">{t('dashboard.donationModal.success.message')}</p>
                        <p className="success-text" style={{ fontSize: '12px' }}>{t('dashboard.donationModal.success.viewDashboard')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="donation-modal-overlay" onClick={handleOverlayClick}>
            <div className="donation-modal">
                <div className="donation-modal-header">
                    <h2 className="donation-modal-title">{t('dashboard.donationModal.title')}</h2>
                    <p className="donation-modal-subtitle">{t('dashboard.donationModal.subtitle')}</p>
                    <button
                        className="donation-modal-close"
                        onClick={onClose}
                        disabled={isProcessing}
                    >×</button>
                </div>

                <div className="donation-modal-body">
                    {/* Project Selection */}
                    <div className="donation-form-group">
                        <label className="donation-form-label">{t('dashboard.donationModal.selectProject')}</label>
                        <select
                            className="donation-form-select"
                            value={selectedProject}
                            onChange={(e) => setSelectedProject(e.target.value)}
                            disabled={isProcessing}
                            required
                        >
                            {projectOptions.map(project => (
                                <option key={project.key} value={project.label}>{project.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Amount Selection */}
                    <div className="donation-form-group">
                        <label className="donation-form-label">{t('dashboard.donationModal.donationAmount')}</label>
                        <div className="donation-amount-grid">
                            {amountOptions.map(option => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`amount-option-btn ${selectedAmount === option.value ? 'active' : ''}`}
                                    onClick={() => setSelectedAmount(option.value)}
                                    disabled={isProcessing}
                                >
                                    <div>{option.label}</div>
                                    {option.usd && (
                                        <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '2px' }}>{option.usd}</div>
                                    )}
                                </button>
                            ))}
                        </div>
                        {selectedAmount === 'custom' && (
                            <input
                                type="number"
                                className="donation-form-input"
                                placeholder={t('dashboard.donationModal.customAmountPlaceholder')}
                                value={customAmount}
                                onChange={(e) => setCustomAmount(e.target.value)}
                                min="1"
                                disabled={isProcessing}
                                required
                                style={{ marginTop: '10px' }}
                            />
                        )}
                    </div>

                    {/* Payment Gateway Selection */}
                    <div className="donation-form-group">
                        <label className="donation-form-label">{t('dashboard.donationModal.selectPaymentGateway')}</label>
                        <div className="payment-methods">
                            <button
                                type="button"
                                className={`payment-method-btn ${paymentGateway === 'razorpay' ? 'active' : ''}`}
                                onClick={() => setPaymentGateway('razorpay')}
                                disabled={isProcessing}
                            >
                                <div className="payment-icon-wrapper">
                                    <img
                                        src="/images/payment/Razorpay.png"
                                        alt="Razorpay"
                                        className="payment-icon razorpay-icon"
                                    />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700 }}>{t('dashboard.donationModal.razorpay')}</div>
                                    <div style={{ fontSize: '10px', opacity: 0.8 }}>{t('dashboard.donationModal.razorpaySubtext')}</div>
                                </div>
                            </button>
                            <button
                                type="button"
                                className={`payment-method-btn ${paymentGateway === 'paypal' ? 'active' : ''}`}
                                onClick={() => setPaymentGateway('paypal')}
                                disabled={isProcessing}
                            >
                                <div className="payment-icon-wrapper">
                                    <img
                                        src="/images/payment/paypal.png"
                                        alt="PayPal"
                                        className="payment-icon"
                                    />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700 }}>{t('dashboard.donationModal.paypal')}</div>
                                    <div style={{ fontSize: '10px', opacity: 0.8 }}>{t('dashboard.donationModal.paypalSubtext')}</div>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Payment Gateway Integration */}
                    <div className="donation-form-group">
                        <div className="payment-gateway-section">
                            {getFinalAmount() > 0 ? (
                                <>
                                    {paymentGateway === 'razorpay' && userData && (
                                        <RazorpayCheckout
                                            amount={getFinalAmount()}
                                            currency="INR"
                                            name={`${userData.firstName} ${userData.lastName}`}
                                            email={userData.email}
                                            phone={userData.phone || ''}
                                            description={selectedProject}
                                            onSuccess={handleRazorpaySuccess}
                                            onError={handleRazorpayError}
                                            onCancel={handleRazorpayCancel}
                                        />
                                    )}

                                    {paymentGateway === 'paypal' && (
                                        <PayPalCheckout
                                            amount={parseFloat(getUSDAmount())}
                                            currency="USD"
                                            onSuccess={handlePayPalSuccess}
                                            onError={handlePayPalError}
                                            onCancel={handlePayPalCancel}
                                        />
                                    )}
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                                    <p>{t('dashboard.donationModal.pleaseSelectAmount')}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Processing Indicator */}
                    {isProcessing && (
                        <div style={{
                            textAlign: 'center',
                            padding: '20px',
                            backgroundColor: '#f0f8ff',
                            borderRadius: '8px',
                            marginTop: '16px'
                        }}>
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                            <p style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>{t('dashboard.donationModal.processing')}</p>
                            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6c757d' }}>{t('dashboard.donationModal.pleaseWait')}</p>
                        </div>
                    )}

                    {/* Security Notice */}
                    <div style={{
                        marginTop: '20px',
                        padding: '12px',
                        backgroundColor: '#f0f8ff',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#495057'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span>🔒</span>
                            <strong>{t('dashboard.donationModal.securePayment')}</strong>
                        </div>
                        <p style={{ margin: 0, lineHeight: '1.4' }}>
                            {t('dashboard.donationModal.securePaymentText', { gateway: paymentGateway === 'razorpay' ? 'Razorpay' : 'PayPal' })}
                        </p>
                    </div>
                </div>

                <div className="donation-modal-footer">
                    <button
                        type="button"
                        className="btn-cancel"
                        onClick={onClose}
                        disabled={isProcessing}
                    >
                        {t('dashboard.donationModal.cancel')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DashboardDonationModal;
